/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useEffect, useRef, useState } from 'react';

import { ActionGroup, ActionType, WatchNowAction } from 'client-code/actions';
import { RecordingStatus } from 'client-code/episode-guide';
import { VideoResolution } from 'client-code/upcoming-offers';
import PropTypes from 'prop-types';

import { COLLECTION_SCREEN_ITEM_RESIZE_TIME } from '../../../config';
import { DimensionScaler } from '../../../core/device/DimensionScaler';
import { BrandingUiInfoKeys } from '../../../core/entities/BrandingUiInfoKeys';
import { ServicesClientImpl } from '../../../core/interactors';
import { Locale, LocaleKeys } from '../../../core/locale';
import { SharedPrefConstants, SharedPreferences } from '../../../core/storage';
import { TActionList } from '../../../core/types';
import { DateTime, UniqueIdentifier } from '../../../core/utils';
import { CollectionFilters } from '../../../store/collectionscreen/CollectionFilters';
import { CollectionSortType } from '../../../store/collectionscreen/CollectionSortType';
import { ListCollectionItemProps } from './ListCollectionItemProps';

import './ListCollectionItem.scss';

const ListCollectionItem: FunctionComponent<ListCollectionItemProps> = ({
    collectionFilterType: type,
    collectionItemData,
    isFocused,
    isSelected,
    isDeleted,
    sortOrderType,
}): JSX.Element => {
    const itemRef = useRef<HTMLDivElement>(null);
    const titleRef = useRef<HTMLDivElement>(null);
    const itemResizeTimer: any = useRef(null);
    const actions: any = useRef(null);

    const [isResizeItem, setIsResizeItem] = useState(false);

    const services = new ServicesClientImpl();
    const newIcon = services.getAppUiImageByKey(BrandingUiInfoKeys.APP_ICONS.NEW_ICON);
    const liveIcon = services.getAppUiImageByKey(BrandingUiInfoKeys.APP_ICONS.LIVE_ICON);

    const {
        seasonOrYear,
        episodeNum,
        episodeNumber,
        isNew,
        isLive,
        title,
        episodeTitle,
        seasonNumber,
        channel,
        firstAiredDate,
        startTime,
        isStreamingAvailable,
        recordingStatus,
        percentWatched,
        recordingId,
        contentId,
        videoResolution,
    } = collectionItemData;

    // This is a hacky fix for overflow. Directly hardcoding new & live icon with margin value here
    // TODO :- Find a better way to acheive this
    let iconWidth = 0;
    if (isNew && isLive) iconWidth = DimensionScaler.getInstance().getScaledWidth(161, false);
    else if ((!isNew && isLive) || (isNew && !isLive))
        iconWidth = DimensionScaler.getInstance().getScaledWidth(76, false);

    const hasOverflowed =
        (titleRef.current?.scrollWidth || 0) + iconWidth > (itemRef.current?.clientWidth || 0);

    const getDateTimeInstance = () =>
        firstAiredDate || startTime ? new DateTime(firstAiredDate || startTime) : null;

    const getWeekDayName = (): string => {
        const dateTime = getDateTimeInstance();
        return dateTime ? dateTime.strftime('%a', 'en') : '';
    };

    const getFormatedDate = (): string => {
        const dateTime = getDateTimeInstance();
        if (dateTime && (firstAiredDate || startTime)) {
            return type === CollectionFilters.UPCOMING_DETAIL
                ? dateTime.strftime('%M/%D')
                : dateTime.strftime('%M/%D/%y');
        }
        return '';
    };

    const getFormatedTime = (): string => {
        const dateTime = getDateTimeInstance();
        return dateTime ? dateTime.to12HourClock() : '';
    };

    const getRecordingIconStyle = (): string => {
        if (isDeleted) return 'lci-rwl-temp-deleted-recording';

        switch (recordingStatus) {
            case RecordingStatus.InProgressRecording:
                return 'lci-rwl-in-progress-recording';
            case RecordingStatus.CompleteKeepForever:
                return 'lci-rwl-complete-keep-forever';
            case RecordingStatus.PartialKeepForever:
                return 'lci-rwl-partial-keep-forever';
            case RecordingStatus.CompleteRecording:
                return 'lci-rwl-complete-recording';
            case RecordingStatus.PartialRecording:
                return 'lci-rwl-partial-recording';
            default:
                return '';
        }
    };

    const getRecordingProgressBarFields = () => (
        <>
            {percentWatched ? (
                <div className="lci-rwl-progress-container">
                    <span className="lci-rwl-progress-bar" />
                    <span
                        className="lci-rwl-progress-bar-position"
                        style={{ marginLeft: `${percentWatched}%` }}
                    />
                </div>
            ) : null}
            {recordingStatus || isDeleted ? (
                <span className={`${getRecordingIconStyle()}`} />
            ) : null}
        </>
    );

    const getSeasonEpisodeField = () => {
        let seasonInfo = '';
        if (!!seasonOrYear && !!episodeNum) {
            seasonInfo = `S${seasonOrYear} E${episodeNum}`;
        } else if (seasonOrYear) {
            seasonInfo = `S${seasonOrYear}`;
        } else if (episodeNum) {
            seasonInfo = `E${episodeNum}`;
        } else {
            seasonInfo = getFormatedDate();
        }
        return <span className="lci-aew-column-field-1">{seasonInfo}</span>;
    };

    const getVideoResolution = () => {
        switch (videoResolution) {
            case VideoResolution.Hd:
                return 'lci-u-resolution-hd';
            case VideoResolution.Sd:
                return 'lci-u-resolution-sd';
            case VideoResolution.Uhd:
                return 'lci-u-resolution-uhd';
            default:
                return 'lci-hidden';
        }
    };

    const renderTitleField = (
        classStyle: string,
        titleField: string,
        shouldShowQuotes: boolean,
    ) => {
        return (
            <>
                <div ref={itemRef} className={`lci-title-container ${classStyle}`}>
                    <div className="lci-title-new-layout">
                        {shouldShowQuotes && <span className="lci-quotes-start">&lsquo;</span>}
                        <span
                            ref={titleRef}
                            className={hasOverflowed ? 'lci-title-overflow' : 'lci-title'}
                        >
                            {titleField}
                        </span>
                        {shouldShowQuotes && <span className="lci-quotes-end">&rsquo;</span>}
                        {isNew && (
                            <span className={`lci-icon ${isLive ? 'lci-new' : ''}`}>
                                <img className={`${newIcon?.imageType}`} src={newIcon?.imageUrl} />
                            </span>
                        )}
                        {isLive && (
                            <span className="lci-icon">
                                <img
                                    className={`${liveIcon?.imageType}`}
                                    src={liveIcon?.imageUrl}
                                />
                            </span>
                        )}
                    </div>
                    {isResizeItem && isSelected && isFocused && (
                        <span className="lci-expand-text">{getBottomTipText()}</span>
                    )}
                </div>
            </>
        );
    };

    const getAllEpisodesItem = () => (
        <>
            {getSeasonEpisodeField()}
            {renderTitleField('lci-aew-title', title, true)}
            {getRecordingProgressBarFields()}
            {isStreamingAvailable ? <span className="lci-wlae-streaming" /> : null}
        </>
    );

    const getRecordingsItem = () => {
        const showSeasonInfo = !!seasonOrYear && !!episodeNum;
        const titleStr = showSeasonInfo ? `S${seasonOrYear} E${episodeNum} ${title}` : title;
        return (
            <>
                <span className="lci-ru-column-field-1">{getWeekDayName()}</span>
                <span className="lci-r-column-field-2">{getFormatedDate()}</span>
                {renderTitleField('lci-r-title', titleStr, true)}
                {getRecordingProgressBarFields()}
                {type === CollectionFilters.WATCH_LIST_DETAILS && isStreamingAvailable ? (
                    <span className="lci-wlae-streaming" />
                ) : null}
            </>
        );
    };

    const getUpcomingItem = () => {
        const showSeasonInfo = !!seasonNumber && !!episodeNumber;
        const episodeTitleStr = episodeTitle || '';
        const titleStr = showSeasonInfo
            ? `S${seasonNumber} E${episodeNumber} ${episodeTitleStr}`
            : episodeTitleStr || title;
        const shouldShowQuotes = !!episodeTitle;
        return (
            <>
                <span className="lci-ru-column-field-1">{getFormatedDate()}</span>
                <span className="lci-u-column-field-2">{getFormatedTime()}</span>
                {renderTitleField('lci-u-title', titleStr, shouldShowQuotes)}
                <span className="lci-u-channel-number">{`${channel?.channelNumber || ''}`}</span>
                <span className={getVideoResolution()} />
            </>
        );
    };

    const getWatchListItem = () => (
        <>
            {getSeasonEpisodeField()}
            {renderTitleField('lci-wl-title', `${title}`, true)}
            {getRecordingProgressBarFields()}
            {isStreamingAvailable ? <span className="lci-wlae-streaming" /> : null}
        </>
    );

    const renderItem = () => {
        switch (type) {
            case CollectionFilters.ALL_EPISODE_DETAIL:
                return getAllEpisodesItem();
            case CollectionFilters.RECORDINGS_DETAILS:
                return sortOrderType && sortOrderType !== CollectionSortType.BY_DATE
                    ? getWatchListItem()
                    : getRecordingsItem();
            case CollectionFilters.WATCH_LIST_DETAILS:
                return sortOrderType && sortOrderType !== CollectionSortType.BY_SEASON
                    ? getRecordingsItem()
                    : getWatchListItem();
            case CollectionFilters.UPCOMING_DETAIL:
                return getUpcomingItem();
            default:
                return '';
        }
    };

    const getActionList = async (): Promise<TActionList> => {
        let res: TActionList;

        if (recordingId)
            res = await services.getRecordingActionsV2({
                recordingId,
                screenType: 'SeriesScreen',
                groups: [ActionGroup.Watch, ActionGroup.ScheduleRecording],
                actionsNetworkCallId: UniqueIdentifier.getNextCollectionActionNetworkCallId(),
            });
        else
            res = await services.getContentActions({
                contentId,
                screenType: 'SeriesScreen',
                groups: [ActionGroup.Watch],
                actionsNetworkCallId: UniqueIdentifier.getNextCollectionActionNetworkCallId(),
            });
        return res;
    };

    const getBottomTipText = (): string => {
        if (type === CollectionFilters.UPCOMING_DETAIL)
            return Locale.getString(LocaleKeys.COLLECTION_ITEM_TIP_TEXT_MORE_INFO);

        let tipText = '';
        const watchNowAction = actions.current?.find(
            (item) => item.actionType === ActionType.WatchNowAction,
        ) as WatchNowAction | undefined;

        if (watchNowAction) {
            if (watchNowAction.hasRecording)
                tipText = Locale.getString(LocaleKeys.COLLECTION_ITEM_TIP_TEXT_RECORDING);
            else if (watchNowAction.isLive)
                tipText = `${Locale.getString(LocaleKeys.COLLECTION_ITEM_TIP_TEXT_WATCH_ON)} Live TV.`;
            else if (watchNowAction.providerList && watchNowAction.providerList.length > 0) {
                const providerName = watchNowAction.providerList[0].displayName;
                tipText = `${Locale.getString(LocaleKeys.COLLECTION_ITEM_TIP_TEXT_WATCH_ON)} ${providerName}.`;
            }
        }

        return tipText;
    };

    const handleOnItemSelect = async () => {
        clearTimeout(itemResizeTimer.current);

        let networkCallId;
        if (type !== CollectionFilters.UPCOMING_DETAIL) {
            const collectionWatchActions = new SharedPreferences(
                SharedPrefConstants.COLLECTION_WATCH_ACTIONS,
            ).edit();
            collectionWatchActions.clear();
            collectionWatchActions.apply();
            const res = await getActionList();
            networkCallId = res.actionsNetworkCallId;
            if (UniqueIdentifier.getCollectionActionNetworkCallId() === networkCallId) {
                actions.current = res.actionList.actions;
                collectionWatchActions.putString(
                    SharedPrefConstants.COLLECTION_WATCH_ACTIONS,
                    JSON.stringify(res.actionList.actions),
                );
                collectionWatchActions.apply();
            }
        }
        const tipText = getBottomTipText();

        // In upcomingfilter fastzap issue will not be there as there is no async operation
        if (
            tipText !== '' &&
            (type === CollectionFilters.UPCOMING_DETAIL ||
                UniqueIdentifier.getCollectionActionNetworkCallId() === networkCallId)
        ) {
            itemResizeTimer.current = setTimeout(() => {
                setIsResizeItem(true);
            }, COLLECTION_SCREEN_ITEM_RESIZE_TIME);
        }
    };

    useEffect(() => {
        if (isSelected && isFocused) {
            handleOnItemSelect();
        } else {
            setIsResizeItem(false);
        }

        return () => {
            setIsResizeItem(false);
            clearTimeout(itemResizeTimer.current);
            actions.current = null;
        };
    }, [isFocused, isSelected, type]);

    return (
        <div
            className={`lci-container ${isResizeItem && isSelected && isFocused ? 'lci-resize-style' : ''}`}
        >
            {renderItem()}
        </div>
    );
};

ListCollectionItem.propTypes = {
    collectionFilterType: PropTypes.string.isRequired,
    collectionItemData: PropTypes.any.isRequired,
    isFocused: PropTypes.bool.isRequired,
    isSelected: PropTypes.bool.isRequired,
};

export default ListCollectionItem;
